import { IDriveUpdate, IReportRepository, ReportParams } from '@common/interfaces';
import AxiosRepository from './axios.repository';

class ReportRepository extends AxiosRepository implements IReportRepository {
  private static buildParams(params?: ReportParams) {
    const searchParams = new URLSearchParams();

    if (params?.agencyId) searchParams.append('agency', params.agencyId);
    if (params?.approvedBy) searchParams.append('approved_by', params.approvedBy);
    if (params?.certifiedBy) searchParams.append('certified_by', params.certifiedBy);
    if (typeof params?.isCarpooled === 'boolean')
      searchParams.append('is_carpooled', params.isCarpooled ? 'True' : 'False');
    if (typeof params?.isRideShared === 'boolean')
      searchParams.append('is_ride_shared', params.isRideShared ? 'True' : 'False');
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.month) searchParams.append('month', String(params.month));
    if (params?.offset) searchParams.append('offset', String(params.offset));
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.pickupDate) searchParams.append('pickup_date_lte', params.pickupDate);
    if (params?.preparedBy) searchParams.append('prepared_by', params.preparedBy);
    if (params?.year) searchParams.append('year', String(params.year));

    return searchParams;
  }

  async getCountryReport(facilityId: string, params?: ReportParams) {
    const searchParams = ReportRepository.buildParams(params);

    const { data } = await this.get(
      `/facilities/${facilityId}/reports/country-report/?${searchParams}`,
    );

    return data;
  }

  async getDriveWithWorkflow(facilityId: string, driveId: string) {
    const { data } = await this.get(
      `/facilities/${facilityId}/reports/cost-recovery-report/drives/${driveId}/workflow/`,
    );

    return data;
  }

  async getMonthlySummary(facilityId: string, params?: ReportParams) {
    const searchParams = ReportRepository.buildParams(params);

    const { data } = await this.get(
      `/facilities/${facilityId}/reports/cost-recovery-report/monthly-summary/?${searchParams}`,
    );

    return data;
  }

  async getReports(facilityId: string, params?: ReportParams) {
    const searchParams = ReportRepository.buildParams(params);

    const { data } = await this.get(
      `/facilities/${facilityId}/reports/cost-recovery-report/?${searchParams}`,
    );

    return data;
  }

  async getSummary(facilityId: string, params?: ReportParams) {
    const searchParams = ReportRepository.buildParams(params);

    const { data } = await this.get(
      `/facilities/${facilityId}/reports/cost-recovery-report/summary/?${searchParams}`,
    );

    return data;
  }

  async createCountryReport(facilityId: string, referenceDate: string) {
    const { data } = await this.post(`/facilities/${facilityId}/reports/country-report/create/`, {
      reference_date: referenceDate,
    });

    return data;
  }

  async exportCountryReport(facilityId: string, params?: ReportParams) {
    const searchParams = ReportRepository.buildParams(params);

    const { data } = await this.get(
      `/facilities/${facilityId}/reports/country-report/export/?${searchParams}`,
      {
        responseType: 'blob',
      },
    );

    return new File([data], 'country_report.csv');
  }

  async exportDrives(facilityId: string, params?: ReportParams) {
    const searchParams = ReportRepository.buildParams(params);
    const searchData = Object.fromEntries(searchParams);

    const { data } = await this.post(
      `/facilities/${facilityId}/reports/cost-recovery-report/export/`,
      {
        ...searchData,
        ...('is_carpooled' in searchData
          ? { is_carpooled: searchData.is_carpooled === 'True' }
          : {}),
        ...('is_ride_shared' in searchData
          ? { is_ride_shared: searchData.is_ride_shared === 'True' }
          : {}),
      },
    );

    return new File([data], 'cost_recovery_report.csv');
  }

  async updateDrive(facilityId: string, driveId: string, updateData: IDriveUpdate) {
    const { data } = await this.patch(
      `/facilities/${facilityId}/reports/cost-recovery-report/drives/${driveId}/workflow/`,
      updateData,
    );

    return data;
  }
}

export default ReportRepository;
